import classNames from 'classnames';
import React, { useState } from 'react';
import Button from '../components/Button';
import Tooltip from '../components/Tooltip';
import PopOver from '../components/deprecated/PopOver';
import PopOverContainer from '../components/deprecated/PopOverContainer';
import commentEmojiIcon from '../icons/non-prefilled/16x16/commentEmoji.svg';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import addTestId from '../utils/addTestId';
import styles from './MagneticWallItem.module.scss';
import LegacyEmojiPicker from './comment/LegacyEmojiPicker';
import showFullPageSheet from './comment/showFullPageSheet';

interface ResponsivePopoverProps {
  target: React.ReactNode;
  content: React.ReactNode;
  open: boolean;
  onClose(): void;
}

const ResponsivePopover = ({
  target,
  content,
  open,
  onClose,
}: ResponsivePopoverProps) => {
  if (!open) {
    return <>{target}</>;
  }

  if (showFullPageSheet()) return null;

  return (
    <PopOver
      placement="left-start"
      popOver={
        <ClickOutsideHandler
          onClickOutside={onClose}
          className={styles.popOverContainer}
        >
          {content}
        </ClickOutsideHandler>
      }
    >
      {target}
    </PopOver>
  );
};

export type MagneticWallItemSize = 'full' | 'compact';

interface MagneticWallItemProps {
  popOverContent: React.ReactNode;
  fullView?: React.ReactNode;
  compactView: React.ReactNode;
  minimizedView?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
  onBlur(): void;
  id: string;
  focused: boolean;
  minimized: boolean;
  wide?: boolean;
  isPopOverOpen: boolean;
  size: MagneticWallItemSize;
  widerLineMargin: number;
}

const MagneticWallItem = ({
  popOverContent,
  fullView,
  compactView,
  minimizedView = compactView,
  id,
  focused,
  minimized,
  wide,
  isPopOverOpen,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onBlur,
  size,
  widerLineMargin,
}: MagneticWallItemProps) => {
  return (
    <div
      data-testid={`magnetic-wall-item-${id}`}
      className={classNames('magnetic-wall-item', {
        [styles.minimized]: minimized,
      })}
    >
      {minimized && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          className={classNames(
            'magnetic-wall-item-minimized-container',
            styles.minimizedContainer,
            {
              focused,
            },
          )}
          style={{ marginLeft: widerLineMargin }}
          onClick={onClick}
        >
          <ResponsivePopover
            onClose={onBlur}
            target={minimizedView}
            content={
              <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
                {popOverContent}
              </div>
            }
            open={isPopOverOpen}
          />
        </div>
      )}
      {size === 'full' && !!fullView && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          className={classNames('magnetic-wall-item-full-container', {
            focused: focused && !minimized,
            'magnetic-wall-item-wide-container': wide && focused,
          })}
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {fullView}
        </div>
      )}
      {size === 'compact' && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          className={classNames('magnetic-wall-item-compact-container', {
            focused: focused && !minimized,
          })}
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <ResponsivePopover
            onClose={onBlur}
            target={compactView}
            content={popOverContent}
            open={isPopOverOpen && !minimized}
          />
        </div>
      )}
    </div>
  );
};

interface MagneticWallItemCardProps {
  children: React.ReactNode;
  focused: boolean;
  focusable?: boolean;
  className?: string;
}
export const MagneticWallItemCard = ({
  children,
  focused,
  focusable,
  className,
}: MagneticWallItemCardProps) => {
  return (
    <div
      className={classNames(
        styles.card,
        {
          [styles.focused]: focused,
          [styles.focusable]: focusable,
        },
        className,
      )}
    >
      {children}
    </div>
  );
};

interface MagneticWallItemWithAvatarProps {
  children: React.ReactNode;
  avatar: React.ReactNode;
  disabled?: boolean;
  innerTestId?: string;
}

export const MagneticWallItemWithAvatar = ({
  children,
  avatar,
  disabled,
  innerTestId,
}: MagneticWallItemWithAvatarProps) => (
  <div
    className={classNames(styles.avatarContainer, {
      [styles.avatarContainerDisabled]: disabled,
    })}
  >
    <div className={styles.avatar}>{avatar}</div>
    <div className={styles.inner} {...addTestId(innerTestId)}>
      {children}
    </div>
  </div>
);

interface MagneticWallItemActionContainerProps {
  children: React.ReactNode;
}

export const MagneticWallItemActionContainer = ({
  children,
}: MagneticWallItemActionContainerProps) => (
  <div className={styles.actionContainer}>{children}</div>
);

interface MagneticWallItemIconButtonProps {
  icon: React.ReactNode;
  onClick(): void;
  tooltip: string;
  tooltipPlacement?: React.ComponentProps<typeof Tooltip>['placement'];
  active?: boolean;
  testId?: string;
  disabled?: boolean;
}

export const MagneticWallItemIconButton = ({
  icon,
  onClick,
  tooltip,
  tooltipPlacement = 'bottom',
  active,
  testId,
  disabled,
}: MagneticWallItemIconButtonProps) => (
  <Tooltip
    placement={tooltipPlacement}
    content={disabled ? undefined : tooltip}
  >
    <Button
      active={active}
      className={classNames(styles.iconButton, { [styles.active]: active })}
      icon={icon}
      onClick={onClick}
      testId={testId}
      aria-label={tooltip}
      disabled={disabled}
    />
  </Tooltip>
);

interface MagneticWallItemSubmitButtonProps {
  onClick(): void;
  disabled: boolean;
  children: React.ReactNode;
}

export const MagneticWallItemSubmitButton = ({
  disabled,
  onClick,
  children,
}: MagneticWallItemSubmitButtonProps) => (
  <Button
    disabled={disabled}
    type="button"
    variant="primary"
    size="small"
    onClick={onClick}
  >
    {children}
  </Button>
);

interface MagneticWallItemSecondaryButtonProps {
  onClick(): void;
  disabled?: boolean;
  children: React.ReactNode;
}

export const MagneticWallItemSecondaryButton = ({
  disabled,
  onClick,
  children,
}: MagneticWallItemSecondaryButtonProps) => (
  <Button
    disabled={disabled}
    type="button"
    variant="secondary"
    size="small"
    onClick={onClick}
  >
    {children}
  </Button>
);

interface MagneticWallItemButtonGroupProps {
  children: React.ReactNode;
}

export const MagneticWallItemButtonGroup = ({
  children,
}: MagneticWallItemButtonGroupProps) => (
  <div className={styles.buttonGroup}>{children}</div>
);

interface MagneticWallItemEmojiPickerButtonProps {
  onPick: (emoji: string) => void;
  tooltipPlacement?: React.ComponentProps<typeof Tooltip>['placement'];
  disabled?: boolean;
}

export const MagneticWallItemEmojiPickerButton = ({
  onPick,
  tooltipPlacement = 'bottom',
  disabled,
}: MagneticWallItemEmojiPickerButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleSetIsOpen = (open: boolean) => {
    setIsOpen(!disabled && open);
  };

  return (
    <PopOver
      placement="bottom"
      open={isOpen}
      onOpenChange={handleSetIsOpen}
      disablePersist
      preventCloseOnContentClick
      trigger="click"
      popOver={
        <PopOverContainer noPadding>
          <LegacyEmojiPicker
            onPick={(emoji) => {
              handleSetIsOpen(false);
              onPick(emoji);
            }}
          />
        </PopOverContainer>
      }
      popOverPortal={document.body}
    >
      <MagneticWallItemIconButton
        tooltip="Insert emoji"
        tooltipPlacement={tooltipPlacement}
        icon={commentEmojiIcon}
        onClick={() => handleSetIsOpen(true)}
        active={isOpen}
        disabled={disabled}
      />
    </PopOver>
  );
};

export default MagneticWallItem;
